import React, { useCallback, useState } from "react"
import { MapStateToProps, MapDispatchToPropsFunction, connect } from "react-redux"
import {
    createStyles,
    makeStyles,
    Theme,
    FormControlLabel,
    Switch,
    FormControl,
    Select,
    MenuItem,
    Drawer,
    Grid,
    Collapse,
    CardContent,
    Typography,
} from "@material-ui/core"
import { IStoreState } from "../Reducers/rootReducer"
import ModalHeader from "./ModalHeader"
import { Roboto } from "shared/theme"
import ErrorHandlerModal from "shared/Modules/Error/Components/ErrorHandlerModal"
import { closeCheckoutModal, ICloseCheckoutModal } from "../Actions/checkoutModalActions"
import TextField from "shared/Components/Form/TextField"
import { IMeetingBookingOrder, IOrder, IResource } from "shared/Types/appTypes"
import ResourceCheckList from "./ResourceCheckList"
import { useDateTime } from "shared/Modules/Localization/useDateTime"
import { StandardFloatingButton } from "shared/Components/Button/StandardFloatingButton"
import { LocalizedStrict } from "shared/Modules/Localization/Components/Localized"
import { createMeeting as doCreateMeeting, ICreateMeeting } from "../../shared/Modules/Meeting/meetingActions"
import Card from "shared/Components/Card/Card"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faChevronDown } from "@fortawesome/pro-light-svg-icons/faChevronDown"
import { faChevronUp } from "@fortawesome/pro-light-svg-icons/faChevronUp"
import { useBoolean } from "mobile/hooks/useBoolean"
// These are the props that can/should be passed from a parent component
// It is the only exported prop interface since all other props are internal
export interface IOwnProps {
    // This component does not have own props
}

// These are the props we expect to be passed from Redux state
interface IStateProps {
    isDrawerOpen: boolean
    resourceFilterValues: any
    shoppingCart: { resources: IResource[] | null }
    editOrder: { order: IMeetingBookingOrder | null }
}

// These re the props used to dispatch actions from the component
interface IDispatchProps {
    hideCheckoutModal: () => ICloseCheckoutModal
    createMeeting: (
        privateMeeting: boolean,
        meetingName: string,
        sittingArrangement: string,
        fromDateTime: string,
        meetingDuration: number
    ) => ICreateMeeting
}

type IProps = IOwnProps & IStateProps & IDispatchProps

const mapStateToProps: MapStateToProps<IStateProps, IOwnProps, IStoreState> = ({
    editOrder,
    shoppingCart,
    resourceFilterValues,
    checkoutModal,
}) => ({
    isDrawerOpen: checkoutModal.open,
    resourceFilterValues: resourceFilterValues.values,
    shoppingCart,
    editOrder,
})
const mapDispatchToProps: MapDispatchToPropsFunction<IDispatchProps, IOwnProps> = (dispatch) => ({
    hideCheckoutModal: () => dispatch(closeCheckoutModal()),
    createMeeting: (
        privateMeeting: boolean,
        meetingName: string,
        sittingArrangement: string,
        fromDateTime: string,
        meetingDuration: number
    ) => {
        return dispatch(doCreateMeeting(privateMeeting, meetingName, sittingArrangement, fromDateTime, meetingDuration))
    },
})

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        container: {
            minHeight: "100%",
            minWidth: "100vw",
            overflow: "auto",
            padding: "10px 15px 90px 15px",
            width: "100vw",
        },
        innerCard: {
            padding: "12px 0",
        },
        root: {
            position: "fixed",
        },
        sittingArrangementFormControl: {
            display: "block",
            marginBottom: 20,
        },
        bookingTimeLabel: {
            color: "#505050",
            fontFamily: Roboto,
            fontSize: theme.typography.body1.fontSize,
            letterSpacing: "0.00938em",
            lineHeight: "16px",
            paddingTop: "12px",
        },
        bookingTimeValue: {
            color: "#505050",
            fontFamily: Roboto,
            fontSize: theme.typography.body1.fontSize,
            fontWeight: 600,
            letterSpacing: "0.00938em",
            lineHeight: "16px",
        },
        collapse: {
            width: "100%",
            minHeight: "30px !important",
            visibility: "visible",
        },
        cardContent: {
            padding: "16px !important",
        },
        card: {
            marginTop: "16px !important",
            marginBottom: "16px !important",
        },
        title: {
            cursor: "pointer",
            marginBottom: theme.spacing(4),

            "& *": {
                cursor: "pointer",
            },
        },
    })
)

const CheckoutContent = (props: IProps & { classes: ReturnType<typeof useStyles> }) => {
    const { editOrder, shoppingCart, resourceFilterValues, hideCheckoutModal, createMeeting, classes } = props

    let defaultPrivateMeetingState = false
    let defaultMeetingName = ""
    if (editOrder.order && editOrder.order.meetingDetails) {
        defaultPrivateMeetingState = editOrder.order.meetingDetails.isPrivate
        defaultMeetingName = editOrder.order.meetingDetails.meetingName
    }

    const [meetingName, setMeetingName] = React.useState(defaultMeetingName)
    const [privateMeeting, setPrivateMeeting] = React.useState(defaultPrivateMeetingState)
    const [sittingArrangement, setSittingArrangement] = React.useState("")
    const dateTimeFactory = useDateTime()

    const changePrivateMeeting = (event: any) => {
        setPrivateMeeting(event.target.checked)
    }

    const meetingDuration = resourceFilterValues.meetingDuration ? resourceFilterValues.meetingDuration : 60
    const bookingStart =
        shoppingCart.resources !== null && shoppingCart.resources.length > 0
            ? dateTimeFactory.fromFNDateTime(shoppingCart.resources[0].bookingSuggestion.startDatetime)
            : dateTimeFactory.now()
    const bookingEnd = bookingStart.plus({ minutes: meetingDuration })

    const sittingArrangementOptions: string[] = []
    if (shoppingCart.resources !== null) {
        // tslint:disable-next-line:prefer-for-of
        for (
            let shoppingCartResourceIndex = 0;
            shoppingCartResourceIndex < shoppingCart.resources.length;
            shoppingCartResourceIndex++
        ) {
            if (
                shoppingCart.resources[shoppingCartResourceIndex] &&
                shoppingCart.resources[shoppingCartResourceIndex].resource &&
                shoppingCart.resources[shoppingCartResourceIndex].resource.room
            ) {
                if (typeof shoppingCart.resources[shoppingCartResourceIndex].resource.room !== "undefined") {
                    if (
                        // @ts-ignore
                        typeof shoppingCart.resources[shoppingCartResourceIndex].resource.room.tableSettings !==
                        "undefined"
                    ) {
                        const possibleOptions =
                            // @ts-ignore
                            shoppingCart.resources[shoppingCartResourceIndex].resource.room.tableSettings
                        for (let i = 0; i < possibleOptions.length; i++) {
                            if (
                                !sittingArrangementOptions.includes(
                                    // @ts-ignore
                                    shoppingCart.resources[shoppingCartResourceIndex].resource.room.tableSettings[i]
                                )
                            ) {
                                sittingArrangementOptions.push(
                                    // @ts-ignore
                                    shoppingCart.resources[shoppingCartResourceIndex].resource.room.tableSettings[i]
                                )
                            }
                        }
                    }
                }
            }
        }
    }

    const onChangeMeetingName = useCallback(
        (event) => {
            setMeetingName(event.target.value)
        },
        [setMeetingName]
    )

    const [isMeetingCollapsed, toggleMeeting] = useBoolean(true)
    const [isResourcesCollapsed, toggleResources] = useBoolean(true)

    return (
        <div role="presentation" className={classes.container}>
            <LocalizedStrict id="completeBooking-header-label" attrs={{ title: true }}>
                <ModalHeader fixed={true} title="Complete booking" onClose={hideCheckoutModal} />
            </LocalizedStrict>

            <LocalizedStrict id="completeBooking-advancedOptions-header-label">
                <p
                    style={{
                        color: "#505050",
                        fontFamily: Roboto,
                        fontSize: "1.125rem", // = 18px @ std
                        fontWeight: 900,
                        letterSpacing: "-0.12px",
                        lineHeight: "21px",
                    }}
                >
                    More options
                </p>
            </LocalizedStrict>
            <Card className={classes.card}>
                <CardContent className={classes.cardContent}>
                    <Collapse in={isMeetingCollapsed} className={classes.collapse}>
                        <Grid
                            item
                            container
                            alignItems="center"
                            justifyContent="space-between"
                            className={classes.title}
                            onClick={toggleMeeting.toggle}
                        >
                            <LocalizedStrict id="completeBooking-meeting-card-meeting-details-title">
                                <Typography variant="h6">Meeting details</Typography>
                            </LocalizedStrict>
                            <FontAwesomeIcon icon={isMeetingCollapsed ? faChevronUp : faChevronDown} />
                        </Grid>
                        <Grid item container direction="column" wrap="nowrap" justifyContent="flex-start">
                            <LocalizedStrict
                                id="delivery-form-meeting-field-label"
                                attrs={{ title: true, placeholder: true }}
                            >
                                <TextField
                                    variant="outlined"
                                    title="Mødenavn"
                                    placeholder="Vælg Mødenavn"
                                    autoFocus={true}
                                    value={meetingName}
                                    onChange={onChangeMeetingName}
                                />
                            </LocalizedStrict>
                            <Grid item alignItems="center">
                                <LocalizedStrict
                                    id="completeBooking-advancedOptions-booking-time"
                                    vars={{
                                        startTime: dateTimeFactory.formatDateTime(bookingStart),
                                        endTime: dateTimeFactory.formatTime(bookingEnd),
                                    }}
                                >
                                    <p className={classes.bookingTimeLabel}>Booking tid:</p>
                                </LocalizedStrict>
                                <p className={classes.bookingTimeValue}>
                                    {dateTimeFactory.formatDateTime(bookingStart)} -{" "}
                                    {dateTimeFactory.formatTime(bookingEnd)}
                                </p>
                            </Grid>
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={privateMeeting}
                                        onChange={changePrivateMeeting}
                                        name="private_meeting"
                                        color="primary"
                                    />
                                }
                                label={
                                    <LocalizedStrict id="completeBooking-advancedOptions-privateMeeting-label">
                                        <p
                                            style={{
                                                color: "#505050",
                                                fontSize: "0.875rem", // = 14px @ std
                                                fontFamily: Roboto,
                                                letterSpacing: "-0.25px",
                                            }}
                                        >
                                            Private meeting
                                        </p>
                                    </LocalizedStrict>
                                }
                            />
                        </Grid>
                    </Collapse>
                </CardContent>
            </Card>
            <Card className={classes.card}>
                <CardContent className={classes.cardContent}>
                    <Collapse in={isResourcesCollapsed} className={classes.collapse}>
                        <Grid
                            item
                            container
                            alignItems="center"
                            justifyContent="space-between"
                            className={classes.title}
                            onClick={toggleResources.toggle}
                        >
                            <LocalizedStrict id="completeBooking-meeting-card-resources-title">
                                <Typography variant="h6">Resources</Typography>
                            </LocalizedStrict>
                            <FontAwesomeIcon icon={isResourcesCollapsed ? faChevronUp : faChevronDown} />
                        </Grid>
                        <ResourceCheckList />
                        <div className={classes.innerCard}>
                            {sittingArrangementOptions.length > 0 ? (
                                <LocalizedStrict id="completeBooking-advancedOptions-sittingsArrangement-label">
                                    <p
                                        style={{
                                            color: "#505050",
                                            fontFamily: Roboto,
                                            fontSize: "0.875rem", // = 14px @ std
                                            fontWeight: 900,
                                            letterSpacing: 0,
                                        }}
                                    >
                                        Sitting arrangement
                                    </p>
                                </LocalizedStrict>
                            ) : (
                                ""
                            )}
                            {sittingArrangementOptions.length > 0 ? (
                                <FormControl
                                    variant="outlined"
                                    style={{
                                        display: "block",
                                        marginBottom: 20,
                                    }}
                                >
                                    <Select
                                        value={sittingArrangement}
                                        onChange={(event: any) => {
                                            setSittingArrangement(event.target.value)
                                        }}
                                        variant="outlined"
                                        displayEmpty
                                        fullWidth
                                    >
                                        <LocalizedStrict id="options-novalue">
                                            <MenuItem value="">-- Select --</MenuItem>
                                        </LocalizedStrict>
                                        {sittingArrangementOptions.map((option: string, key) => {
                                            return (
                                                <MenuItem key={key} value={option}>
                                                    {option}
                                                </MenuItem>
                                            )
                                        })}
                                    </Select>
                                </FormControl>
                            ) : (
                                ""
                            )}
                        </div>
                    </Collapse>
                </CardContent>
            </Card>

            <LocalizedStrict id="completeBooking-advancedOptions-button-complete">
                <StandardFloatingButton
                    disabled={!meetingName || shoppingCart.resources === null || shoppingCart.resources.length === 0}
                    onClick={() => {
                        if (!meetingName || shoppingCart.resources === null || shoppingCart.resources.length === 0)
                            return
                        createMeeting(
                            privateMeeting,
                            meetingName,
                            sittingArrangement,
                            dateTimeFactory.toFNDateTime(bookingStart),
                            meetingDuration
                        )
                    }}
                >
                    Komplet
                </StandardFloatingButton>
            </LocalizedStrict>
        </div>
    )
}

const CheckoutModal = (props: IProps) => {
    const { isDrawerOpen, hideCheckoutModal } = props
    const classes = useStyles()
    const rootAttributes = {
        onClose: hideCheckoutModal,
        open: isDrawerOpen,
        className: classes.root,
    }

    return (
        <div>
            <Drawer anchor="right" {...rootAttributes}>
                <ErrorHandlerModal close={hideCheckoutModal}>
                    <CheckoutContent {...props} classes={classes} />
                </ErrorHandlerModal>
            </Drawer>
        </div>
    )
}

export default connect(mapStateToProps, mapDispatchToProps)(CheckoutModal)
